import React, { useState, useCallback, forwardRef, useImperativeHandle, memo } from "react"
import Dialog from "@material-ui/core/Dialog"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import MuiDialogContent from "@material-ui/core/DialogContent"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import { withStyles, makeStyles } from "@material-ui/core/styles"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import HandleExcelSheet from "@/components/project-dashboard/HandleExcelSheet"
import Button from "@material-ui/core/Button"
import { bulkAddParticipant } from "@/redux/actions/participantAction"
import { useSelector, useDispatch } from "react-redux"
import BulkAddParticipantReview from "./BulkAddParticipantReview"

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(5),
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
        width: "28px",
        height: "28px",
        "&:hover": {
            backgroundColor: theme.palette.primary.secondary,
        },
    },
})

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    )
})

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(0),
    },
}))(MuiDialogContent)

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        zIndex: "1",
    },
    contentWrapper: {
        padding: theme.spacing(0, 3),
        paddingBottom: theme.spacing(3),
        margin: "0 auto",
        maxWidth: "650px",
        textAlign: "center",
        "&>p": {
            marginBottom: theme.spacing(2),
        },
    },
    contentTitle: {
        marginBottom: theme.spacing(4),
    },
}))

const ModalBulkAddParticipant = memo(
    forwardRef((props, ref) => {
        const { modalBulkAddShow, handleClose } = props
        const classes = useStyles()
        const [participantFinalListAddParticipant, setParticipantFinalListAddParticipant] = useState([])
        const { projects } = useSelector((state) => state.projectReducer)
        const [showResult, setShowResult] = useState(false)

        const handleSetParticipantListAddParticipant = useCallback(
            (participants) => {
                setParticipantFinalListAddParticipant((prev) => [...prev, ...participants])
            },
            [participantFinalListAddParticipant, showResult]
        )

        const resetAll = () => {
            setShowResult(false)
            setParticipantFinalListAddParticipant([])
        }

        useImperativeHandle(ref, () => ({
            resetAll,
        }))

        const dispatch = useDispatch()
        const handleCancel = () => {
            handleClose()
            resetAll()
        }

        const handleConfirm = useCallback(() => {
            const { projectId } = projects[0]
            dispatch(bulkAddParticipant(participantFinalListAddParticipant, projectId))
            setShowResult(true)
        }, [participantFinalListAddParticipant])

        const bulkAddContent = () => {
            return (
                <>
                    <Typography variant="h6" component="h2" className={classes.contentTitle} color="textPrimary">
                        Bulk Add Participant
                    </Typography>
                    <Typography component="p">
                        Select your participant list file and upload all the participants you want to include in your project
                    </Typography>
                    <Box className={classes.participantBulkUploadContent} mt={4}>
                        <HandleExcelSheet handleSetParticipantListAddParticipant={handleSetParticipantListAddParticipant} />
                    </Box>
                    <Box className={classes.buttonWrapper} m={4} display="flex" justifyContent="center" alignItems="center">
                        <Button variant="outlined" color="primary" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Box>&nbsp;&nbsp;</Box>
                        <Button variant="contained" color="primary" onClick={handleConfirm}>
                            Start Upload
                        </Button>
                    </Box>
                </>
            )
        }

        return (
            <Dialog disableScrollLock aria-labelledby="bulk-add-participant" open={modalBulkAddShow} maxWidth="md" fullWidth>
                <DialogTitle className={classes.closeButton} id="bulk-add-participant" onClose={handleClose} />
                <DialogContent className={classes.dialogContent}>
                    <Box className={classes.contentWrapper}>
                        {!showResult ? (
                            bulkAddContent()
                        ) : (
                            <BulkAddParticipantReview
                                handleClose={handleClose}
                                participantList={participantFinalListAddParticipant}
                                setShowResult={setShowResult}
                                setParticipantFinalListAddParticipant={setParticipantFinalListAddParticipant}
                            />
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        )
    })
)

export default ModalBulkAddParticipant
